<template>
  <transition name="ai-modal-slide">
    <div v-if="isOpen" class="ai-modal-overlay" @click.self="closeModal">
      <div class="ai-modal-content">
        <button class="ai-modal-close-btn" @click="closeModal" aria-label="閉じる">&times;</button>
        <div class="ai-modal-header">
          <img src="@/assets/ai.png" alt="AI Avatar" class="ai-icon">
          <h2 class="ai-modal-title">AI英先生</h2>
          <button v-if="isLoggedIn" class="ai-modal-clear-btn" @click="clearConversation" aria-label="会話をクリア">
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
        <template v-if="isLoggedIn">
          <div class="ai-modal-body">
            <div class="chat-messages" ref="chatMessagesContainer">
              <div v-for="(message, index) in messages" :key="index" :class="['message', message.role]">
                <div class="message-bubble">
                  <div class="markdown" v-html="renderMarkdown(message.content, message.role === 'assistant' && isLoading && index === messages.length - 1)"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="ai-modal-footer">
            <textarea
              ref="userInputRef"
              v-model="userInput"
              class="chat-input"
              placeholder="英語に関する質問を入力するか、英語で話しかけてみましょう..."
              rows="1"
              @input="adjustTextareaHeight"
              @keydown.enter.prevent="handleEnterKey"
            ></textarea>
            <button @click="sendMessage" class="send-button" :disabled="isLoading || !userInput.trim()">
              <i class="fas fa-paper-plane"></i>
            </button>
          </div>
        </template>
        <template v-else>
          <div class="auth-prompt-section">
             <div class="prompt-icon">
               <i class="fas fa-info-circle"></i>
             </div>
             <h3 class="prompt-title">ログインが必要です</h3>
             <p class="prompt-message">AI英先生をご利用いただくには、ログインが必要です。</p>
             <div class="prompt-actions">
                <button @click="goToLogin" class="btn btn-primary btn-small">ログイン / 新規登録</button>
             </div>
           </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue';
import { marked } from 'marked';
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css'; // Or choose another theme
import DOMPurify from 'dompurify';
import { useRouter } from 'vue-router';
import { auth } from '@/api';

// eslint-disable-next-line no-undef
const props = defineProps({ isOpen: Boolean });
// eslint-disable-next-line no-undef
const emit = defineEmits(['close']);

const router = useRouter();

const messages = ref([]);
const userInput = ref('');
const isLoading = ref(false);
const chatMessagesContainer = ref(null);
const userInputRef = ref(null);

// --- Animation State ---
const animationQueue = ref([]); // Queue of characters to type
const typingInterval = ref(null);
const streamFinished = ref(false); // Flag to indicate stream end
const TYPING_SPEED_MS = 50; // Adjust speed (milliseconds per character)
// -----------------------

const API_URL = 'https://api.lightai.io/v1/chat/completions';
// WARNING: Avoid hardcoding API keys in production code. Consider using environment variables.
const API_KEY = 'sk-KvutOMM13OJtdRay5e911597D0Bb4f93980f8b3614Bf27Df';
const MODEL = 'deepseek-v3-0324';

const SYSTEM_PROMPT = "あなたは「AI英先生」という名前の、英語学習をサポートする優秀なAIアシスタントです。\\n" +
                      "あなたの役割は、ユーザーからの英語に関する質問に日本語で答えること、そしてユーザーと英語での会話練習を行うことです。\\n" +
                      "英語学習に関係のない質問（例えば、歴史、科学、個人的な意見、プログラミングなど）には、絶対に答えてはいけません。\\n" +
                      "そのような質問を受けた場合は、「申し訳ありませんが、私は英語学習に関する質問にのみお答えできます。」のように、丁寧かつ簡潔に断ってください。\\n" +
                      "他の話題に誘導されても、決して応じず、英語学習の話題に戻すようにしてください。\\n" +
                      "回答は常に日本語で行いますが、ユーザーが英語での会話練習を求めた場合は、英語で応答してください。\\n" +
                      "回答はMarkdown形式で、読みやすく、整理されているようにしてください。コードブロックは適切に使用してください。";

// Finite State Machine for Code Block Detection (from your document)
const States = {
    text: 0, // 文本状态
    codeStartSm: 1, // 小代码块状态
    codeStartBig: 2, // 大代码块状态
}

function isInCode(text) {
    let state = States.text
    let source = text
    let inStart = true // 是否处于文本开始状态，即还没有消费过文本
    while (source) { // 当文本被解析消费完后，就是个空字符串了，就能跳出循环
        let char = source.charAt(0) // 取第 0 个字
        switch (state) {
            case States.text:
                if (/^\n?```/.test(source)) {
                    if (inStart || source.startsWith('\n')) {
                        state = States.codeStartBig
                    }
                    source = source.replace(/^\n?```/, '')
                } else if (char === '\\') {
                    source = source.slice(2)
                } else if (char === '`') {
                    state = States.codeStartSm
                    source = source.slice(1)
                } else {
                    source = source.slice(1)
                }
                inStart = false
                break
            case States.codeStartSm:
                if (char === '`') {
                    state = States.text
                    source = source.slice(1)
                } else if (char === '\\') {
                    source = source.slice(2)
                } else {
                    source = source.slice(1)
                }
                break
            case States.codeStartBig:
                if (/^\n```/.test(source)) {
                    state = States.text
                    source = source.replace(/^\n```/, '')
                } else {
                    source = source.slice(1)
                }
                break
        }
    }
    return state !== States.text
}

// Configure marked with highlight.js
marked.setOptions({
  highlight: function (code, lang) {
    const language = hljs.getLanguage(lang) ? lang : 'plaintext';
    try {
        return hljs.highlight(code, { language, ignoreIllegals: true }).value;
    } catch (error) {
        console.error('Highlighting error:', error);
        return hljs.highlightAuto(code).value; // Fallback
    }
  },
  gfm: true,
  breaks: true
});

// Store initial message for clearing
const initialAssistantMessage = { role: 'assistant', content: 'こんにちは！私はAI英先生です。英語に関する質問があれば、何でも聞いてくださいね。英語での会話練習もできますよ。' };

// Initialize messages with the initial greeting
messages.value = [initialAssistantMessage];

const scrollToBottom = () => {
  nextTick(() => {
    if (chatMessagesContainer.value) {
      chatMessagesContainer.value.scrollTop = chatMessagesContainer.value.scrollHeight;
    }
  });
};

const adjustTextareaHeight = () => {
  const textarea = userInputRef.value;
  if (textarea) {
    textarea.style.height = 'auto'; // Reset height
    textarea.style.height = `${textarea.scrollHeight}px`; // Set to scroll height
  }
};

// Renamed from formatMessageContent to renderMarkdown
const renderMarkdown = (text, showCursor = false) => {
  // Default to empty string if text is null/undefined
  const rawText = text || '';

  // Parse markdown to HTML
  let html;
  try {
      // IMPORTANT: Parse the raw text first
      html = marked.parse(rawText);
  } catch (e) {
      console.error("Markdown parsing error:", e);
      // Fallback: Escape the raw text to prevent XSS if marked fails
      html = rawText.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  // Sanitize the HTML generated by marked
  const sanitizedHtml = DOMPurify.sanitize(html);

  // Add cursor if needed
  const cursorSpan = '<span class="cursor"></span>';
  if (showCursor && !isInCode(rawText)) {
      return sanitizedHtml + cursorSpan;
  }

  return sanitizedHtml;
};

// --- Typing Animation Functions ---
const startTypingAnimation = (messageIndex) => {
    if (typingInterval.value) return; // Already running

    typingInterval.value = setInterval(() => {
        if (animationQueue.value.length > 0) {
            const char = animationQueue.value.shift(); // Get next char
            if (messages.value[messageIndex]) {
                messages.value[messageIndex].content += char;
                // Don't need to scroll on every char? Maybe only sometimes?
                // Let's keep it for now to ensure visibility
                scrollToBottom();
            } else {
                // Message missing, stop animation
                console.warn("Target message for animation not found, stopping.");
                clearInterval(typingInterval.value);
                typingInterval.value = null;
                animationQueue.value = [];
                streamFinished.value = true; // Assume stream is done if message gone
                checkAnimationAndStreamEnd();
            }
        } else {
            // Queue is empty, stop the interval
            clearInterval(typingInterval.value);
            typingInterval.value = null;
            checkAnimationAndStreamEnd(); // Check if loading should stop
        }
    }, TYPING_SPEED_MS);
};

const checkAnimationAndStreamEnd = () => {
    if (streamFinished.value && animationQueue.value.length === 0 && !typingInterval.value) {
        isLoading.value = false;
        console.log("Stream ended and animation queue empty. Loading finished.");
    }
};
// -------------------------------

// --- Clear Conversation --- 
const clearConversation = () => {
    if (!isLoggedIn.value) return;
    console.log("Clearing conversation...");
    // Stop any ongoing animation
    if (typingInterval.value) {
        clearInterval(typingInterval.value);
        typingInterval.value = null;
    }
    // Clear the queue
    animationQueue.value = [];
    // Reset flags
    isLoading.value = false;
    streamFinished.value = false;
    // Reset messages to only the initial one
    messages.value = [initialAssistantMessage];
    // Scroll to top after clearing (optional, could also scroll to bottom)
    nextTick(() => {
        if (chatMessagesContainer.value) {
            chatMessagesContainer.value.scrollTop = 0;
        }
    });
    // Focus input again
    nextTick(() => {
         userInputRef.value?.focus();
    });
};
// ------------------------

const sendMessage = async () => {
  if (!isLoggedIn.value) return;
  const trimmedInput = userInput.value.trim();
  if (!trimmedInput || isLoading.value) return;

  // Add user message
  messages.value.push({ role: 'user', content: trimmedInput });
  userInput.value = '';
  adjustTextareaHeight();
  scrollToBottom();

  // --- Reset animation state ---
  isLoading.value = true;
  streamFinished.value = false;
  animationQueue.value = [];
  if (typingInterval.value) {
      clearInterval(typingInterval.value);
      typingInterval.value = null;
  }
  // ---------------------------

  const apiMessages = [
    { role: 'system', content: SYSTEM_PROMPT },
    ...messages.value.slice(-10).map(m => ({ role: m.role, content: m.content }))
  ];

  // Add placeholder for the assistant response
  messages.value.push({ role: 'assistant', content: '' });
  const assistantMessageIndex = messages.value.length - 1;
  scrollToBottom();

  console.log("Sending API Messages:", JSON.stringify(apiMessages, null, 2));

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: MODEL,
        messages: apiMessages,
        stream: true // Enable streaming
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})); // Try to parse error JSON
      throw new Error(`APIエラー: ${response.status} ${response.statusText}. ${errorData.error?.message || ''}`);
    }

    if (!response.body) {
        throw new Error('レスポンスボディがありません。');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventData = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const { done, value } = await reader.read();
        // Important: Check done flag *before* processing buffer, especially if value might be empty
        if (done) {
            console.log("Stream reader finished (done flag).");
            break;
        }

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex;
        while ((newlineIndex = buffer.search(/\r?\n/)) >= 0) {
            const line = buffer.substring(0, newlineIndex);
            buffer = buffer.substring(newlineIndex + (buffer[newlineIndex] === '\r' ? 2 : 1));

            if (line.startsWith('data:')) {
                currentEventData += line.substring(line[5] === ' ' ? 6 : 5) + '\n';
            } else if (line === '' && currentEventData.trim() !== '') {
                const dataToProcess = currentEventData.trim();
                currentEventData = '';

                if (dataToProcess === '[DONE]') {
                    console.log("Stream finished with [DONE]");
                    streamFinished.value = true;
                    checkAnimationAndStreamEnd(); // Check if animation also finished
                    // Important: Break the *inner* while loop after processing DONE event's empty line
                    // The outer loop will then check the 'done' flag from reader.read()
                    // Actually, we need to stop reading further, so break outer loop might be needed
                    // Let's signal outer loop to break
                    buffer = ''; // Clear buffer as we are done
                    break; // Break the inner line processing loop
                } else {
                    try {
                        const chunk = JSON.parse(dataToProcess);
                        if (chunk.choices && chunk.choices[0]?.delta?.content) {
                            const contentChunk = chunk.choices[0].delta.content;
                            // Add characters to the queue instead of directly appending
                            animationQueue.value.push(...contentChunk.split(''));
                            // Start animation if not already running
                            if (!typingInterval.value) {
                                startTypingAnimation(assistantMessageIndex);
                            }
                        }
                    } catch (e) {
                        console.error('JSON解析エラー:', e, 'データ:', `'${dataToProcess}'`);
                    }
                }
            } else if (line.startsWith('event:') || line.startsWith('id:') || line.startsWith('retry:') || line.startsWith(':')) {
                // Ignore
            } else if (line !== '') {
                console.warn("Unexpected SSE line:", line);
            }
        } // end line processing loop
        // If DONE was processed and broke the inner loop, check here to break outer loop
        if (streamFinished.value) break;
    } // end stream reading loop

    // Mark stream as finished after the loop naturally ends (if no [DONE])
    streamFinished.value = true;
    checkAnimationAndStreamEnd(); // Check if animation finished

  } catch (error) {
    console.error('AI応答の取得中にエラー:', error);
    streamFinished.value = true; // Mark as finished on error
    if (messages.value[assistantMessageIndex]) {
        messages.value[assistantMessageIndex].content += `\nエラーが発生しました: ${error.message}`;
    }
    // Stop animation and clear queue on error
    if (typingInterval.value) clearInterval(typingInterval.value);
    typingInterval.value = null;
    animationQueue.value = [];
    isLoading.value = false; // Stop loading immediately on error
  } finally {
     // isLoading is now managed by checkAnimationAndStreamEnd or catch block
     // Remove isLoading = false from here
    scrollToBottom();
    nextTick(() => {
        userInputRef.value?.focus();
    });
  }
};

const handleEnterKey = (event) => {
  if (!event.shiftKey) { // Send on Enter, new line on Shift+Enter
    sendMessage();
  } else {
    // Allow default behavior for Shift+Enter (new line)
    // Might need to manually insert newline depending on textarea behavior
     const textarea = event.target;
     const start = textarea.selectionStart;
     const end = textarea.selectionEnd;
     textarea.value = textarea.value.substring(0, start) + "\\n" + textarea.value.substring(end); // Use \n instead of \r\n
     textarea.selectionStart = textarea.selectionEnd = start + 1;
     adjustTextareaHeight();
  }
};


const closeModal = () => {
  emit('close');
};

// Add ESC key listener to close modal
const handleEscKey = (event) => {
  if (event.key === 'Escape' && props.isOpen) {
    closeModal();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleEscKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscKey);
});

// Watch for modal opening to focus input and scroll
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    nextTick(() => {
      if (isLoggedIn.value) {
          userInputRef.value?.focus();
          scrollToBottom();
          adjustTextareaHeight();
      }
    });
  } else {
      // Modal closed, stop animation if running
       if (typingInterval.value) {
         clearInterval(typingInterval.value);
         typingInterval.value = null;
       }
       animationQueue.value = [];
       isLoading.value = false;
       streamFinished.value = false;
  }
});

// Watch for new messages to scroll down
watch(messages, () => {
  scrollToBottom();
}, { deep: true });

// --- Computed Property for Login Status ---
const isLoggedIn = computed(() => !!auth.currentUser);
// ------------------------------------------

// --- Navigation for Auth Prompt ---
const goToLogin = () => {
    router.push('/auth');
    closeModal(); // Close the modal after navigating
};
// --------------------------------

</script>

<style scoped>
/* Basic Reset/Defaults */
.ai-modal-content * {
  box-sizing: border-box;
}

.ai-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050; /* Higher than other elements */
  padding: 15px;
}

.ai-modal-content {
  background-color: #fff;
  border-radius: 12px;
  max-width: 600px; /* Limit max width */
  width: 100%;
  max-height: 85vh; /* Limit max height */
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden; /* Prevents content overflow before body scroll */
  position: relative; /* For close button positioning */
}

.ai-modal-header {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background-color: #f8f9fa; /* Light header background */
}

.ai-icon {
  width: 30px; /* Adjust size as needed */
  height: 30px;
  margin-right: 10px;
  border-radius: 50%; /* Make it round */
  object-fit: cover; /* Ensure image covers the area */
  vertical-align: middle; /* Align vertically with title */
}

.ai-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.ai-modal-close-btn {
  position: absolute;
  top: 8px;
  right: 45px;
  background: none;
  border: none;
  font-size: 28px;
  color: #aaa;
  cursor: pointer;
  line-height: 1;
  padding: 5px;
  z-index: 11;
}

.ai-modal-close-btn:hover {
  color: #333;
}

.ai-modal-clear-btn {
  position: absolute;
  top: 8px;
  right: 12px;
  background: none;
  border: none;
  font-size: 18px;
  color: #aaa;
  cursor: pointer;
  line-height: 1;
  padding: 8px;
  z-index: 10;
}

.ai-modal-clear-btn:hover {
  color: var(--primary-color);
}

.ai-modal-body {
  flex-grow: 1;
  overflow-y: auto; /* Enable scrolling for messages */
  padding: 15px 20px;
  background-color: #ffffff; /* Ensure body bg is white */
}

.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message {
  display: flex;
  max-width: 85%;
  word-wrap: break-word; /* Ensure long words break */
}

.message.user {
  align-self: flex-end;
  margin-left: auto; /* Push user messages right */
}

.message.assistant {
  align-self: flex-start;
  margin-right: auto; /* Push assistant messages left */
}

.message-bubble {
  padding: 10px 15px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.5;
  position: relative; /* For loading dots */
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  overflow-wrap: break-word;
  white-space: pre-wrap; /* Preserve whitespace and wrap */
}

/* Apply markdown styles */
.message-bubble :deep(p) {
  margin-top: 0;
  margin-bottom: 0.5em;
}
.message-bubble :deep(p:last-child) {
  margin-bottom: 0;
}
.message-bubble :deep(ul),
.message-bubble :deep(ol) {
  padding-left: 20px;
  margin-bottom: 0.5em;
}
.message-bubble :deep(li) {
  margin-bottom: 0.2em;
}
.message-bubble :deep(pre) {
  background-color: #f0f0f0;
  padding: 8px 12px;
  border-radius: 6px;
  overflow-x: auto;
  font-size: 13px;
  margin: 0.5em 0;
}
.message-bubble :deep(code) {
  font-family: 'Courier New', Courier, monospace;
  background-color: #f0f0f0; /* Light background for inline code */
  padding: 0.2em 0.4em;
  border-radius: 4px;
  font-size: 0.9em;
}
.message-bubble :deep(pre) > code {
  background-color: transparent; /* Code inside pre shouldn't have extra bg */
  padding: 0;
  font-size: inherit; /* Inherit size from pre */
}
.message-bubble :deep(a) {
  color: var(--primary-color);
  text-decoration: underline;
}


.message.user .message-bubble {
  background-color: var(--primary-color);
  color: white;
  border-bottom-right-radius: 5px;
}

.message.assistant .message-bubble {
  background-color: #e9ecef;
  color: #333;
  border-bottom-left-radius: 5px;
}

.ai-modal-footer {
  display: flex;
  align-items: flex-end; /* Align items to bottom */
  padding: 10px 15px;
  border-top: 1px solid #eee;
  background-color: #f8f9fa;
}

.chat-input {
  flex-grow: 1;
  border: 1px solid #ced4da;
  border-radius: 20px;
  padding: 10px 15px;
  font-size: 14px;
  resize: none; /* Disable manual resize */
  overflow-y: auto; /* Allow scroll if content exceeds max height */
  max-height: 100px; /* Limit max height before scrolling */
  line-height: 1.4;
  margin-right: 10px;
  outline: none;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.075);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.chat-input:focus {
  border-color: var(--primary-color-light);
  box-shadow: 0 0 0 0.2rem rgba(255, 143, 171, 0.25); /* Focus ring */
}

.send-button {
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s ease, transform 0.1s ease;
  flex-shrink: 0; /* Prevent button from shrinking */
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.send-button:hover {
  background-color: var(--primary-color-dark);
}

.send-button:active {
  transform: scale(0.95);
}

.send-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Loading Dots Animation */
.loading-dots {
  display: inline-block;
}
.loading-dots span {
  opacity: 0;
  animation: blink 1s infinite;
  font-size: 1.5em; /* Make dots bigger */
  line-height: 1;
}
.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}
.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}

/* Transition */
.ai-modal-slide-enter-active,
.ai-modal-slide-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease; /* Increased duration slightly */
}
.ai-modal-slide-enter-from,
.ai-modal-slide-leave-to {
  opacity: 0;
  /* Start off-screen to the right, slide out to the right */
  transform: translateX(50px); /* Start slightly off-screen, adjust as needed */
}

/* Optional: Style overlay fade separately if needed */
/* .ai-modal-overlay {
  transition: background-color 0.4s ease;
} */

/* Responsive adjustments */
@media (max-width: 768px) {
  .ai-modal-content {
    max-height: 90vh; /* Slightly more height on mobile */
    margin: 10px; /* Add margin */
    width: calc(100% - 20px); /* Use available width */
  }
  .ai-modal-header {
    padding: 12px 15px;
  }
  .ai-modal-title {
    font-size: 16px;
  }
  .ai-icon {
    width: 24px; /* Slightly smaller on mobile */
    height: 24px;
  }
  .ai-modal-close-btn {
    font-size: 22px; /* Slightly smaller */
    top: 6px; /* Adjust vertical position */
    right: 40px; /* Increase space from right for clear button */
    padding: 4px;
  }
  .ai-modal-clear-btn {
      font-size: 16px; /* Smaller clear icon */
      top: 7px; /* Adjust vertical position */
      right: 8px;
      padding: 6px;
  }
  .ai-modal-body {
    padding: 10px 15px;
  }
  .message-bubble {
    font-size: 13px;
    padding: 8px 12px;
  }
   .message-bubble :deep(pre), .message-bubble :deep(code) {
     font-size: 12px;
   }
  .message {
     max-width: 90%; /* Slightly wider messages on mobile */
  }
  .ai-modal-footer {
    padding: 8px 10px;
  }
  .chat-input {
    padding: 8px 12px;
    font-size: 13px;
    max-height: 80px;
  }
  .send-button {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
}

/* Markdown and Code Highlighting Styles (converted from your Tailwind examples) */
.markdown {
  line-height: 1.75rem; /* leading-7 */
}

.markdown :deep(pre) {
  background-color: #282c34; /* bg-[#282c34] */
  padding: 1rem; /* p-4 */
  margin-top: 1rem; /* mt-4 */
  border-radius: 0.375rem; /* rounded-md */
  color: white;
  width: 100%;
  overflow-x: auto;
}

.markdown :deep(code) {
  /* width: 100%; */ /* Usually not needed for inline code */
  font-family: 'Courier New', Courier, monospace; /* Added for consistency */
}

.markdown :deep(p) {
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
}

.markdown :deep(p:first-child) {
  margin-top: 0;
}

/* Inline code blocks */
.markdown :deep(:not(pre) > code) {
  background-color: #f0f0f0; /* Adjusted for light theme, was bg-[#282c34] */
  color: #c7254e; /* Adjusted for light theme, was text-[#e06c75] */
  padding: 0.2em 0.4em; /* px-1 py-[2px] equivalent */
  border-radius: 0.25rem; /* rounded-md */
  font-size: 0.9em;
}

/* List styles */
.markdown :deep(ol) {
  list-style-type: decimal;
  padding-left: 40px;
}

.markdown :deep(ul) {
  list-style-type: disc;
  padding-left: 40px;
}

/* Cursor Style */
.markdown :deep(.cursor) {
  display: inline-block;
  width: 2px;
  height: 1.25em; /* Adjusted from 20px to match line height */
  background-color: #333; /* bg-gray-800 */
  animation: blink 1.2s step-end infinite;
  margin-left: 2px;
  vertical-align: text-bottom; /* Adjusted from sub */
}

/* Dark mode cursor color (optional, if you have dark mode) */
/* @media (prefers-color-scheme: dark) {
  .markdown :deep(.cursor) {
    background-color: #f0f0f0; 
  }
} */

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* Ensure message bubble adapts */
.message-bubble {
  padding: 0; /* Remove padding as markdown container will handle it */
  background-color: transparent; /* Let markdown div handle background? Or keep? */
  box-shadow: none; /* Remove bubble shadow if markdown handles it */
  white-space: normal; /* Let markdown handle wrapping */
}

.message.user .message-bubble .markdown,
.message.assistant .message-bubble .markdown {
    padding: 10px 15px;
    border-radius: 18px;
    font-size: 14px;
    line-height: 1.5;
    position: relative;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    overflow-wrap: break-word;
    white-space: pre-wrap; /* Keep pre-wrap for markdown */
}

.message.user .message-bubble .markdown {
  background-color: var(--primary-color);
  color: white;
  border-bottom-right-radius: 5px;
}

.message.assistant .message-bubble .markdown {
  background-color: #e9ecef;
  color: #333;
  border-bottom-left-radius: 5px;
}

/* Ensure code highlighting theme is applied */
@import 'highlight.js/styles/github-dark.css'; /* Or your chosen theme */

/* Textarea Scrollbar Styling */
.chat-input::-webkit-scrollbar {
  width: 6px; /* Make scrollbar thinner */
}

.chat-input::-webkit-scrollbar-track {
  background: transparent; /* Hide track */
  border-radius: 3px;
}

.chat-input::-webkit-scrollbar-thumb {
  background-color: #ccc; /* Light grey thumb */
  border-radius: 3px;
  border: 1px solid transparent; /* Optional: adds slight padding */
}

.chat-input::-webkit-scrollbar-thumb:hover {
  background-color: #aaa; /* Darker grey on hover */
}

/* Firefox scrollbar styling (optional but good practice) */
.chat-input {
  scrollbar-width: thin; /* "auto" or "thin" */
  scrollbar-color: #ccc transparent; /* thumb track */
}

/* Styles for the Auth Prompt Section (if different from AuthPromptModal) */
.auth-prompt-section {
  padding: 40px 20px; /* Add padding within the modal body */
  text-align: center;
}

/* Re-use prompt styles from AuthPromptModal or define similar ones */
.prompt-icon {
  font-size: 36px;
  color: var(--primary-color);
  margin-bottom: 15px;
}

.prompt-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.prompt-message {
  color: #666;
  font-size: 14px;
  margin-bottom: 25px;
  line-height: 1.6;
}

.prompt-actions {
  display: flex;
  justify-content: center; /* Center button */
}

/* Button styling assumed to be available globally or define here */
.btn-primary {
    /* styles */
}
.btn-small {
    /* styles */
}

</style> 